/**
 * CapabilityTakeAssessmentView — multi-section assessment form with three
 * question types (yes/no, rating_5/likert_5, multi-select + radio fallback).
 * Pure presentational; the parent owns `answers`, `currentSection`, and
 * submission state.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ChevronLeft, ChevronRight, CheckCircle, Loader2, Send } from 'lucide-react';
import { RatingScale } from './RatingScale';
import { YesNoToggle } from './YesNoToggle';
import { MultiSelectQuestion } from './MultiSelectQuestion';

export const CapabilityTakeAssessmentView = ({
  selectedAssessment,
  sections,
  section,
  currentSection,
  answers,
  answeredInSection,
  totalInSection,
  totalAnswered,
  totalQuestions,
  submitting,
  setView,
  setCurrentSection,
  onAnswer,
  onSubmit,
}) => {
  return (
    <div data-testid="take-assessment">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setView('list')} data-testid="exit-assessment"><ChevronLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-lg font-semibold text-[#0A0A0A]">{selectedAssessment?.title}</h1>
            <p className="text-xs text-[#9CA3AF]">Section {currentSection + 1} of {sections.length} — {totalAnswered}/{totalQuestions} answered</p>
          </div>
        </div>
        <Badge variant="secondary" className="text-xs">{Math.round((totalAnswered / totalQuestions) * 100)}% complete</Badge>
      </div>

      <Progress value={(totalAnswered / totalQuestions) * 100} className="mb-6 h-2" />

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <Card className="border-[#E5E7EB] h-fit"><CardContent className="p-2">
          {sections.map((s, idx) => {
            const answered = s.questions.filter(q => answers[`${s.section_id}_${q.question_id}`]).length;
            const complete = answered === s.questions.length;
            return (
              <button
                key={s.section_id}
                onClick={() => setCurrentSection(idx)}
                data-testid={`section-btn-${idx}`}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm transition-colors text-left ${currentSection === idx ? 'bg-[#002FA7] text-white' : 'text-[#4B5563] hover:bg-[#F3F4F6]'}`}
              >
                {complete ? (
                  <CheckCircle className={`w-4 h-4 flex-shrink-0 ${currentSection === idx ? 'text-white' : 'text-[#34C759]'}`} />
                ) : (
                  <span className={`w-4 h-4 flex-shrink-0 rounded-full border-2 text-[10px] flex items-center justify-center font-bold ${currentSection === idx ? 'border-white text-white' : 'border-[#E5E7EB] text-[#9CA3AF]'}`}>{idx + 1}</span>
                )}
                <span className="truncate">{s.title}</span>
                <span className={`ml-auto text-[10px] ${currentSection === idx ? 'text-white/70' : 'text-[#9CA3AF]'}`}>{answered}/{s.questions.length}</span>
              </button>
            );
          })}
        </CardContent></Card>

        <Card className="border-[#E5E7EB]">
          <CardHeader className="border-b border-[#E5E7EB]">
            <CardTitle className="text-base">{section?.title}</CardTitle>
            {section?.description && <p className="text-sm text-[#4B5563]">{section.description}</p>}
            <div className="flex items-center gap-2 mt-2">
              <Progress value={(answeredInSection / totalInSection) * 100} className="flex-1 h-1.5" />
              <span className="text-xs text-[#9CA3AF]">{answeredInSection}/{totalInSection}</span>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="overflow-y-auto pr-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
              <div className="space-y-6">
                {section?.type === 'yes_no' ? (
                  (() => {
                    const categories = {};
                    section.questions.forEach(q => { const cat = q.category || 'General'; if (!categories[cat]) categories[cat] = []; categories[cat].push(q); });
                    return Object.entries(categories).map(([cat, qs]) => (
                      <div key={cat}>
                        <h3 className="text-sm font-semibold text-[#002FA7] uppercase tracking-wider mb-3 pb-1 border-b border-[#E5E7EB]">{cat}</h3>
                        <div className="space-y-3">
                          {qs.map((q) => (
                            <div key={q.question_id} className="flex items-center justify-between gap-4 py-1" data-testid={`q-${q.question_id}`}>
                              <span className="text-sm text-[#4B5563] flex-1">{q.text}</span>
                              <YesNoToggle value={answers[`${section.section_id}_${q.question_id}`] || ''} onChange={(v) => onAnswer(section.section_id, q.question_id, v)} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()
                ) : section?.type === 'rating_5' || section?.type === 'likert_5' ? (
                  section.questions.map((q) => (
                    <div key={q.question_id} className="pb-4 border-b border-[#F3F4F6] last:border-0" data-testid={`q-${q.question_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1"><p className="text-sm font-medium text-[#0A0A0A]">{q.text}</p>{q.description && <p className="text-xs text-[#9CA3AF] mt-1">{q.description}</p>}</div>
                        <RatingScale value={answers[`${section.section_id}_${q.question_id}`] || ''} onChange={(v) => onAnswer(section.section_id, q.question_id, v)} labels={section.type === 'likert_5' ? ['Inefficient', 'Excellent'] : ['Least', 'Most']} />
                      </div>
                    </div>
                  ))
                ) : (
                  section?.questions.map((q) => (
                    <div key={q.question_id} className="pb-4 border-b border-[#F3F4F6] last:border-0" data-testid={`q-${q.question_id}`}>
                      <p className="text-sm font-medium text-[#0A0A0A] mb-3">{q.text}</p>
                      {q.type === 'multi_select' ? (
                        <MultiSelectQuestion
                          options={q.options || []}
                          value={answers[`${section.section_id}_${q.question_id}`] || ''}
                          onChange={(v) => onAnswer(section.section_id, q.question_id, v)}
                          questionId={q.question_id}
                        />
                      ) : (
                        <RadioGroup
                          value={answers[`${section.section_id}_${q.question_id}`] || ''}
                          onValueChange={(v) => onAnswer(section.section_id, q.question_id, v)}
                        >
                          <div className="grid grid-cols-1 gap-2">
                            {q.options?.map((opt) => (
                              <div key={opt} className="flex items-center gap-3">
                                <RadioGroupItem value={opt} id={`${q.question_id}_${opt}`} />
                                <Label htmlFor={`${q.question_id}_${opt}`} className="text-sm text-[#4B5563] cursor-pointer">{opt}</Label>
                              </div>
                            ))}
                          </div>
                        </RadioGroup>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#E5E7EB]">
              <Button variant="outline" onClick={() => setCurrentSection(Math.max(0, currentSection - 1))} disabled={currentSection === 0} data-testid="prev-section-btn">
                <ChevronLeft className="w-4 h-4 mr-1" />Previous
              </Button>
              {currentSection < sections.length - 1 ? (
                <Button onClick={() => setCurrentSection(currentSection + 1)} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="next-section-btn">
                  Next<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={onSubmit} disabled={submitting || totalAnswered === 0} className="bg-[#34C759] hover:bg-[#2DA647]" data-testid="submit-assessment-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Submitting...</> : <><Send className="w-4 h-4 mr-1" />Submit Assessment</>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
